import api from "@/lib/api";

export const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

export async function getPushStatus() {
  try {
    const { data } = await api.get("/push/status");
    return data;
  } catch (e) {
    return { enabled: false, devices: 0 };
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser does not support notifications");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was blocked");

  const registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  await navigator.serviceWorker.ready;

  const { data } = await api.get("/push/public-key");
  const publicKey = data.publicKey;
  if (!publicKey) throw new Error("Push is not configured on the server");

  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return true;
}

export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

export async function sendTestPush() {
  await api.post("/push/test");
}

export const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
  window.navigator.standalone === true;

export async function getNotifyPrefs() {
  const { data } = await api.get("/notifications/preferences");
  return {
    preferences: data.preferences || { assigned: true, status: true, comment: true },
    email: data.email || { assigned: true, status: true, comment: true },
  };
}

export async function saveNotifyPrefs({ preferences, email }) {
  const { data } = await api.put("/notifications/preferences", { preferences, email });
  return data;
}
